import { DXF_BASEMAP_CONTEXT_VERSION, type DxfBasemapContext } from './types';

const TEXT_ENCODING = 'utf-8';

function assertCompressionStreamSupport(kind: 'compress' | 'decompress') {
  if (kind === 'compress' && typeof CompressionStream === 'undefined') {
    throw new Error('Current browser does not support CompressionStream');
  }
  if (kind === 'decompress' && typeof DecompressionStream === 'undefined') {
    throw new Error('Current browser does not support DecompressionStream');
  }
}

async function streamBlob(blob: Blob, stream: GenericTransformStream) {
  return await new Response(blob.stream().pipeThrough(stream)).blob();
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isDxfBasemapContext(value: unknown): value is DxfBasemapContext {
  if (!isObject(value)) return false;

  return (
    value.version === DXF_BASEMAP_CONTEXT_VERSION &&
    value.coordinateSpace === 'editor-mm-centered-v1' &&
    isObject(value.bounding) &&
    isObject(value.layers) &&
    Array.isArray(value.entities) &&
    isObject(value.blocks) &&
    Array.isArray(value.usedBlockNames) &&
    isObject(value.metadata) &&
    isObject(value.stats)
  );
}

export async function compressDxfBasemapContext(context: DxfBasemapContext) {
  assertCompressionStreamSupport('compress');

  const json = JSON.stringify(context);
  const source = new Blob([json], { type: 'application/json' });
  return await streamBlob(source, new CompressionStream('gzip'));
}

export async function decompressDxfBasemapContext(buffer: ArrayBuffer) {
  assertCompressionStreamSupport('decompress');

  const source = new Blob([buffer], { type: 'application/gzip' });
  const decompressed = await streamBlob(source, new DecompressionStream('gzip'));
  const json = await decompressed.text();
  const parsed = JSON.parse(json) as unknown;

  if (!isDxfBasemapContext(parsed)) {
    throw new Error('Invalid DXF basemap context');
  }

  return parsed;
}

export async function readDxfBasemapContextFromPlainJson(buffer: ArrayBuffer) {
  const json = new TextDecoder(TEXT_ENCODING).decode(buffer);
  const parsed = JSON.parse(json) as unknown;

  if (!isDxfBasemapContext(parsed)) {
    throw new Error('Invalid DXF basemap context');
  }

  return parsed;
}
